import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, RotateCcw, ShieldCheck } from 'lucide-react';

const ProgressCheckpointElement = ({ config, element, onComplete, onSaveData, isCompleted }) => {
  const pc = config.progress_checkpoint || config || {};
  const questions = pc.questions || [];
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);
  const [allCorrect, setAllCorrect] = useState(false);

  const getQuestionText = (q) => q.question_text || q.question || '';
  const getOptionText = (opt) => typeof opt === 'string' ? opt : opt.text || opt.label || '';
  
  const getCorrectIndex = (q) => {
    // Support both: q.correct (index) or options[i].is_correct (boolean)
    if (typeof q.correct === 'number') return q.correct;
    const opts = q.options || [];
    const idx = opts.findIndex(o => o.is_correct || o.isCorrect);
    return idx >= 0 ? idx : 0;
  };

  const handleCheck = () => {
    setChecked(true);
    const correct = questions.every((q, qi) => answers[qi] === getCorrectIndex(q));
    setAllCorrect(correct);
    onSaveData({ answers, correct });
    if (correct && !isCompleted) { onComplete(); toast.success('Checkpoint passed!'); }
  };

  const handleRetry = () => { setAnswers({}); setChecked(false); setAllCorrect(false); };

  return (
    <div className="space-y-4" data-testid="progress-checkpoint-element">
      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0" />
        <p className="text-sm font-medium text-amber-800">{pc.message || 'Quick check — answer correctly to continue'}</p>
      </div>

      {questions.map((q, qi) => {
        const correctIdx = getCorrectIndex(q);
        const userAnswer = answers[qi];
        const isCorrect = checked && userAnswer === correctIdx;
        const isWrong = checked && userAnswer !== undefined && userAnswer !== correctIdx;
        const opts = q.options || [];

        return (
          <div key={`cp-${qi}`} className={`p-4 rounded-xl border-2 ${isCorrect ? 'border-green-300 bg-green-50' : isWrong ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}>
            <p className="font-medium text-sm text-slate-900 mb-3">{getQuestionText(q)}</p>
            <div className="space-y-1.5">
              {opts.map((opt, oi) => {
                const isThisCorrect = oi === correctIdx;
                return (
                  <label key={`cp-${qi}-${oi}`} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-sm transition-colors ${
                    checked && isThisCorrect ? 'border-green-400 bg-green-50 font-medium' :
                    checked && userAnswer === oi && !isThisCorrect ? 'border-red-400 bg-red-50' :
                    userAnswer === oi ? 'border-[#20acf6] bg-blue-50' :
                    'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name={`checkpoint-${qi}`}
                      checked={userAnswer === oi}
                      onChange={() => !checked && setAnswers(prev => ({ ...prev, [qi]: oi }))}
                      disabled={checked && allCorrect}
                      className="accent-[#20acf6]"
                    />
                    <span>{getOptionText(opt)}</span>
                    {checked && isThisCorrect && <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />}
                  </label>
                );
              })}
            </div>
            {isWrong && <p className="mt-2 text-xs text-red-600 font-medium">Incorrect — review the material above and try again.</p>}
          </div>
        );
      })}

      {!checked ? (
        <Button onClick={handleCheck} disabled={Object.keys(answers).length < questions.length} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="check-checkpoint-btn">
          <ShieldCheck className="h-4 w-4 mr-2" /> Check Answers
        </Button>
      ) : !allCorrect && (
        <Button onClick={handleRetry} variant="outline" data-testid="retry-checkpoint-btn">
          <RotateCcw className="h-4 w-4 mr-2" /> Try Again
        </Button>
      )}
    </div>
  );
};

export default ProgressCheckpointElement;
